import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Info, Lock } from "lucide-react";

import { api, formatApiError } from "../lib/api";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import ProjectPicker from "./ProjectPicker";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useAuth } from "../contexts/AuthContext";
import { isAttendanceOfficer } from "../lib/rbac";

const STATUS_OPTIONS = [
    { value: "present", label: "Present" },
    { value: "late", label: "Late" },
    { value: "absent", label: "Absent" },
    { value: "partial", label: "Partial / Half-day" },
    { value: "exception", label: "Exception" },
    { value: "weekend", label: "Weekend" },
    { value: "holiday", label: "Holiday" },
];

const FIELDS = [
    { key: "status", label: "Status" },
    { key: "check_in_time", label: "Check-in (HH:MM)" },
    { key: "check_out_time", label: "Check-out (HH:MM)" },
    { key: "project_id", label: "Project" },
    { key: "late_minutes", label: "Late minutes" },
    { key: "ot_hours", label: "OT hours" },
    { key: "pay_multiplier", label: "Pay multiplier" },
    { key: "notes", label: "Notes" },
];

// Iter69 — Fields the Attendance Officer cannot toggle via bulk edit.
// Mirrors the backend's `_OFFICER_BULK_HARD_LOCKED_FIELDS` set:
//   Status, Check-in, Late minutes, OT hours, Pay multiplier, Notes.
// Officer sees these fields with a padlock chip + disabled Apply
// checkbox instead of being hidden — hidden fields lead to
// "why can't I see it?" support tickets; a lock is self-explanatory.
// `check_out_time` is EDITABLE for Officer via bulk (backend allows
// it iff every selected row currently has last_clock_out=null —
// mass-completing missed checkouts is the sanctioned use case).
const OFFICER_LOCKED_FIELDS = new Set([
    "status",
    "check_in_time",
    "late_minutes", "ot_hours", "pay_multiplier",
    "notes",
]);

/**
 * BulkEditAttendanceDialog — Iter48
 * Only fields with the "Apply" checkbox checked get sent to the server.
 * Requires an explicit confirm step before writing.
 */
export default function BulkEditAttendanceDialog({ open, onOpenChange, ids, moduleId, onSaved }) {
    const { user } = useAuth();
    const officerScope = isAttendanceOfficer(user);
    const [enabled, setEnabled] = useState({});
    const [values, setValues] = useState({});
    const [projects, setProjects] = useState([]);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setEnabled({});
        setValues({});
        setConfirming(false);
        if (moduleId) {
            api.get("/projects", { params: { module_id: moduleId } })
                .then(({ data }) => setProjects(Array.isArray(data) ? data : (data?.items || [])))
                .catch(() => setProjects([]));
        }
    }, [open, moduleId]);

    const enabledCount = useMemo(() => Object.values(enabled).filter(Boolean).length, [enabled]);

    const buildChanges = () => {
        const changes = {};
        for (const f of FIELDS) {
            if (!enabled[f.key]) continue;
            const v = values[f.key];
            switch (f.key) {
                case "status":
                case "notes":
                case "check_in_time":
                case "check_out_time":
                case "project_id":
                    changes[f.key] = v === undefined || v === "__none__" ? null : v;
                    break;
                case "late_minutes":
                    changes[f.key] = v === "" || v === undefined ? null : Number(v);
                    break;
                case "ot_hours":
                case "pay_multiplier":
                    changes[f.key] = v === "" || v === undefined ? null : Number(v);
                    break;
                default:
                    changes[f.key] = v;
            }
        }
        return changes;
    };

    const onProceed = (e) => {
        e.preventDefault();
        if (enabledCount === 0) {
            toast.error("Enable at least one field to apply");
            return;
        }
        setConfirming(true);
    };

    const onCommit = async () => {
        setSaving(true);
        try {
            const { data } = await api.post("/attendance/bulk-edit", {
                ids,
                changes: buildChanges(),
            });
            const ok = data.updated || 0;
            const errs = data.errors?.length || 0;
            if (ok) toast.success(`Bulk edit applied to ${ok} row${ok === 1 ? "" : "s"}${errs ? ` · ${errs} skipped` : ""}`);
            else toast.error(`No rows updated${errs ? ` · ${errs} error(s)` : ""}`);
            onOpenChange(false);
            onSaved && onSaved(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const renderInput = (key) => {
        const v = values[key] ?? "";
        const on = enabled[key];
        const commonProps = { disabled: !on };
        switch (key) {
            case "status":
                return (
                    <Select value={v || undefined} onValueChange={(val) => setValues((s) => ({ ...s, [key]: val }))} disabled={!on}>
                        <SelectTrigger data-testid={`bulk-edit-value-${key}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                );
            case "project_id":
                // Iter67 F1 — Universal ProjectPicker with search on
                // Job # or name. Empty string → "no project" applied
                // to the whole selection.
                return (
                    <div className={on ? "" : "opacity-50 pointer-events-none"}>
                        <ProjectPicker
                            projects={(projects || []).filter((p) => p.active !== false)}
                            value={v || ""}
                            onValueChange={(val) => setValues((s) => ({ ...s, [key]: val }))}
                            allowNone
                            disabled={!on}
                            testid={`bulk-edit-value-${key}`}
                        />
                    </div>
                );
            case "check_in_time":
            case "check_out_time":
                return <Input type="time" value={v} onChange={(e) => setValues((s) => ({ ...s, [key]: e.target.value }))} {...commonProps} data-testid={`bulk-edit-value-${key}`} />;
            case "notes":
                return <Textarea rows={2} value={v} onChange={(e) => setValues((s) => ({ ...s, [key]: e.target.value }))} {...commonProps} data-testid={`bulk-edit-value-${key}`} />;
            default:
                return <Input type="number" step={key === "pay_multiplier" ? "0.05" : (key === "ot_hours" ? "0.25" : "1")} min="0" value={v} onChange={(e) => setValues((s) => ({ ...s, [key]: e.target.value }))} {...commonProps} data-testid={`bulk-edit-value-${key}`} />;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="bulk-edit-attendance-dialog">
                <DialogHeader>
                    <DialogTitle>Bulk edit attendance · {ids.length} row{ids.length === 1 ? "" : "s"}</DialogTitle>
                    <div className="text-xs text-[color:var(--asl-muted)] mt-1">
                        Toggle the checkbox for each field you want to change. Unchecked fields will be left as-is per row.
                    </div>
                </DialogHeader>

                {!confirming && (
                    <form onSubmit={onProceed} className="space-y-2">
                        {/* Iter69 — Officer info banner. All bulk changes
                            an Officer commits route through Dept Mgr
                            approval per the existing p54 stage machine
                            (rows land in `submitted` / `pending`).
                            Surfacing it here prevents "why did my bulk
                            edit not go live?" support tickets. */}
                        {officerScope && (
                            <div
                                className="rounded-md bg-sky-50 border border-sky-200 p-3 text-xs flex gap-2"
                                data-testid="bulk-edit-officer-banner"
                            >
                                <Info className="h-4 w-4 text-sky-700 mt-0.5 flex-shrink-0" />
                                <div className="text-sky-900">
                                    Bulk changes made by Attendance Officer require Dept Manager approval.
                                    Only rows with a missing check-out will accept a new time in bulk — populated rows are rejected.
                                </div>
                            </div>
                        )}
                        {FIELDS.map((f) => {
                            const locked = officerScope && OFFICER_LOCKED_FIELDS.has(f.key);
                            return (
                                <div
                                    key={f.key}
                                    className={`grid grid-cols-[160px_auto_1fr] gap-3 items-center py-1.5 border-b last:border-b-0 ${locked ? "opacity-60" : ""}`}
                                    data-testid={`bulk-edit-row-${f.key}`}
                                >
                                    <Label className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={!!enabled[f.key]}
                                            disabled={locked}
                                            onCheckedChange={(v) => setEnabled((s) => ({ ...s, [f.key]: !!v }))}
                                            data-testid={`bulk-edit-toggle-${f.key}`}
                                        />
                                        <span className="flex items-center gap-1.5">
                                            {f.label}
                                            {locked && (
                                                <TooltipProvider delayDuration={150}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span
                                                                className="inline-flex items-center gap-0.5 rounded-sm bg-slate-100 px-1 py-0.5 text-[9px] font-medium text-slate-600"
                                                                data-testid={`bulk-edit-locked-${f.key}`}
                                                                aria-label="Locked — requires Dept Manager"
                                                            >
                                                                <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" className="text-[11px]">
                                                            Locked — requires Dept Manager
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}
                                        </span>
                                    </Label>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                        {locked
                                            ? "locked (officer)"
                                            : enabled[f.key] ? "Apply →" : "leave unchanged"}
                                    </div>
                                    <div>{renderInput(f.key)}</div>
                                </div>
                            );
                        })}
                    </form>
                )}

                {confirming && (
                    <div className="space-y-3 py-1">
                        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm flex gap-2" data-testid="bulk-edit-confirm-warning">
                            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                            <div>
                                <div className="font-semibold text-amber-900">Apply {enabledCount} field change{enabledCount === 1 ? "" : "s"} to {ids.length} row{ids.length === 1 ? "" : "s"}?</div>
                                <div className="text-amber-800 text-xs mt-1">
                                    Any approved rows in this selection will be reverted to Pending and require re-approval.
                                    Bulk edits to IN/OUT or Status on biometric rows will stamp them as Manually adjusted. Other field changes (project, notes) leave the biometric source intact.
                                </div>
                            </div>
                        </div>
                        <div className="text-xs text-slate-600">
                            <div className="font-semibold mb-1">Fields being changed:</div>
                            <ul className="space-y-0.5 mono">
                                {Object.entries(enabled).filter(([, on]) => on).map(([k]) => (
                                    <li key={k} data-testid={`bulk-edit-summary-${k}`}>
                                        <span className="text-slate-500">{k}:</span>{" "}
                                        <strong>{String(values[k] ?? "(cleared)")}</strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {!confirming && (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-edit-cancel">Cancel</Button>
                            <Button onClick={onProceed} disabled={enabledCount === 0} data-testid="bulk-edit-review">
                                Review changes ({enabledCount})
                            </Button>
                        </>
                    )}
                    {confirming && (
                        <>
                            <Button variant="outline" onClick={() => setConfirming(false)} data-testid="bulk-edit-back">← Back</Button>
                            <Button onClick={onCommit} disabled={saving} data-testid="bulk-edit-commit">
                                {saving ? "Applying…" : `Apply to ${ids.length}`}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
